<template>
  <dx-popup
    :visible="visible"
    :title="getTitle"
    :width="800"
    :height="getModelHeight"
    :showCloseButton="false"
    :dragEnabled="false"
    :resizeEnabled="false"
    @hidden="onPopupHide"
    @shown="onPopupShown"
    :wrapper-attr="{
      class: 'css-v2 gc-popup gc-popup-copy-bom',
    }"
  >
    <DxToolbarItem
      v-for="(item, index) in getToolbarItems"
      :key="index"
      :widget="item.widget"
      :location="item.location"
      :visible="item.visible"
      :toolbar="item.position"
      :options="item.options"
      :template="item.template ? item.template : null"
    />
    <template #content>
      <div ref="mainDiv">
        <div class="current-bom css-v2 gc-popup-form">
          <gc-form
            :form-options="{
              gcLabelLocation: 'left',
            }"
            ref="gc-header-form-copy-planning"
            :form-items="getFormItems"
            :is-normal="true"
            :data-model.sync="model"
            :form-mode="'create'"
            :from-from="'gc-header-form-d-a'"
            :form-height="getFormHeight"
            :checkValid="isValid"
            :valueChangeCallBack="valueChangeCallBack"
          >
          </gc-form>
        </div>

        <DxLoadPanel
          :position="{ of: '#container-wrapper' }"
          :visible.sync="loadingVisible"
          :show-indicator="true"
          :show-pane="true"
          :shading="true"
          :hide-on-outside-click="false"
          shading-color="rgba(0,0,0,0.3)"
        />
      </div>
    </template>
  </dx-popup>
</template>
<script>
import { mapGetters } from "vuex";
import { BranchService } from "../../_services/api/sales/branch.service";
import { DivisionService } from "../../_services/api/sales/division.service";
import { forwardTabbing } from "../../_helpers/ui/keyboard";
import { ProductionPlanningService } from "../../_services/api/production/production-planning.service";
import { CompanyService } from "../../_services/api/accounts/company.service";
import { IsResponseDataValid } from "../../_helpers/ui/helpers";
export default {
  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    title: {
      type: String,
      default: "Copy Product",
    },
    popUpConfig: {
      type: Object,
      default: () => ({}),
    },
    showLoading: {
      type: Function,
      default: () => {},
    },
    displayValidationSummary: {
      type: Function,
      default: () => {},
    },
    displayServerError: {
      type: Function,
      default: () => {},
    },
    refreshList: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      copyTitle: "Copy Production Plan from",
      model: {
        CompanyName: null,
        BranchName: null,
        DivisionName: null,
        CurrentWeek: null,
        DateIn: null,
        ToWeek: null,
        CompanyCode: null,
        BranchCode: null,
        DivisionCode: null,
      },
      loadingVisible: false,
      isValid: false,
    };
  },
  methods: {
    /**
     * On Popup Hide
     */
    onPopupHide() {
      this.$store.dispatch("hotKey/setHotKeyPopupMode", false);
      this.$emit("setCopyPlanningVisible", false);
    },

    /**
     * On Popup Shown
     */
    async onPopupShown() {
      await this.$store.dispatch("screenFactors/getFactors", [18]);
      this.$store.dispatch("hotKey/setHotKeyPopupMode", true);
      this.loadingVisible = true;

      if (this.popUpConfig) {
        if (this.popUpConfig.DateIn) {
          this.copyTitle = `Copy Production Plan from ${this.getCurrentWeek(
            this.popUpConfig.DateIn
          )}`;
          this.model.CurrentWeek = this.getCurrentWeek(this.popUpConfig.DateIn);
        }

        if (this.popUpConfig.BranchCode) {
          const branch = await BranchService.getByCode(
            this.popUpConfig.BranchCode,
            {
              select: ["Code", "Description"],
            }
          );
          if (
            branch &&
            branch.data &&
            branch.data.value &&
            branch.data.value.length > 0
          ) {
            this.model.BranchName = branch.data.value[0].Description;
            this.model.BranchCode = branch.data.value[0].Code;
          }
        }

        if (this.popUpConfig.DivisionCode) {
          const division = await DivisionService.getByCode(
            this.popUpConfig.DivisionCode,
            {
              select: ["Code", "Description"],
            }
          );

          if (
            division &&
            division.data &&
            division.data.value &&
            division.data.value.length > 0
          ) {
            this.model.DivisionName = division.data.value[0].Description;
            this.model.DivisionCode = division.data.value[0].Code;
          }
        }
      }

      if (this.companyName) {
        this.model.CompanyName = this.companyName;
      }

      const companyResponse = await CompanyService.getCompany({
        select: ["Code", "Name"],
      });

      if (IsResponseDataValid(companyResponse)) {
        this.model.CompanyName = companyResponse.data.value[0].Name;
        this.model.CompanyCode = companyResponse.data.value[0].Code;
      }

      this.$nextTick(() => {
        this.loadingVisible = false;
        this.focusOnForm();
      });
    },

    /**
     * Get Current Week
     * @param {Date} date
     * @returns {String}
     */
    getCurrentWeek(date) {
      const weekRange = this.getWeekRange(date);
      return `${new Date(
        weekRange.startDate + ":00:00:00"
      ).toShortDateString()} - ${new Date(
        weekRange.endDate + ":00:00:00"
      ).toShortDateString()}`;
    },

    /**
     * Convert input to local date (strip time and timezone)
     * @param {Date|string|number} input
     * @returns {Date} local date
     */
    toLocalDate(input) {
      if (!input && input !== 0) return new Date(NaN);

      if (input instanceof Date) {
        // Create a new Date with the same local Y/M/D (strip time)
        return new Date(input.getFullYear(), input.getMonth(), input.getDate());
      }

      if (typeof input === "string") {
        // Strictly support ISO-like "YYYY-MM-DD" first (common source of bugs)
        const m = input.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (m) {
          const year = Number(m[1]);
          const month = Number(m[2]) - 1; // zero-based
          const day = Number(m[3]);
          return new Date(year, month, day); // local date (no timezone shift)
        }

        // Try common separators (YYYY/MM/DD or YYYY.MM.DD or DD-MM-YYYY)
        // eslint-disable-next-line no-useless-escape
        const parts = input.split(/[-\/\.]/).map((p) => p.trim());
        if (parts.length >= 3) {
          // guess format: if first part length 4 => YYYY/MM/DD
          if (parts[0].length === 4) {
            return new Date(
              Number(parts[0]),
              Number(parts[1]) - 1,
              Number(parts[2])
            );
          }
          // fallback: attempt Date constructor (less reliable)
          const d = new Date(input);
          return new Date(d.getFullYear(), d.getMonth(), d.getDate());
        }

        // Last resort
        const d = new Date(input);
        return new Date(d.getFullYear(), d.getMonth(), d.getDate());
      }

      // fallback for numbers etc.
      const d = new Date(input);
      return new Date(d.getFullYear(), d.getMonth(), d.getDate());
    },

    /**
     * Get Week Range (Monday to Sunday) for a given date
     * @param {Date} inputDate
     * @returns {Object} { startDate: 'YYYY-MM-DD', endDate: 'YYYY-MM-DD' }
     */
    getWeekRange(inputDate) {
      const date = this.toLocalDate(inputDate);
      if (isNaN(date)) return { startDate: null, endDate: null };

      const day = date.getDay(); // 0 = Sun, 1 = Mon, ...

      const diffToMonday = day === 0 ? -6 : 1 - day;

      const monday = new Date(date);
      monday.setDate(date.getDate() + diffToMonday);

      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);

      const fmt = (d) =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
          2,
          "0"
        )}-${String(d.getDate()).padStart(2, "0")}`;

      return {
        startDate: fmt(monday),
        endDate: fmt(sunday),
      };
    },

    /**
     * Focus on Form
     */
    focusOnForm() {
      setTimeout(() => {
        this.$nextTick(() => {
          if (
            this.$refs["gc-header-form-copy-planning"] &&
            this.$refs["gc-header-form-copy-planning"].$refs["gc-form-comp"]
          ) {
            const formInstance =
              this.$refs["gc-header-form-copy-planning"].$refs["gc-form-comp"]
                .instance;
            if (formInstance) {
              const firstElement = formInstance.getEditor("DateIn");

              if (firstElement) {
                firstElement.focus();
              }
            }
          }
        });
      }, 150);
    },

    /**
     * On Copy Date Changed
     * @param {Object} e
     */
    onCopyDateChanged(e) {
      this.model.ToWeek = this.getCurrentWeek(e.value);
    },

    /**
     * On Copy Handler
     * @param {Object} e
     */
    onCopyHandler(e) {
      if (!e) return;

      this.isValid = true;
    },

    /**
     * Value Change Call Back
     * @param {Boolean} isValid
     * @param {Array} brokenRules
     */
    async valueChangeCallBack(isValid, brokenRules) {
      // const currentSaveType = this.SaveType;

      var validations = "";
      this.isValid = false;

      if (!isValid) {
        brokenRules.forEach((element) => {
          validations += `<div style="width: 400px;">${element.message}</div>`;
        });

        try {
          await this.$alert(validations, "Validation Error", {
            width: 400,
          });
        } catch (error) {
          // User might have pressed ESC, but we still need to reset
        }

        // Reset SaveType when validation fails
        this.SaveType = undefined;

        this.$nextTick(() => {
          this.$refs["mainDiv"].focus();
        });
      } else {
        this.checkIfDestinationWeekExists();
      }
    },

    /**
     * Check If Destination Week Exists
     */
    async checkIfDestinationWeekExists() {
      this.loadingVisible = true;
      try {
        const checkExisits =
          await ProductionPlanningService.checkProductionPlanningExists(
            this.getWeekRange(this.model.DateIn).startDate,
            this.getWeekRange(this.model.DateIn).endDate
          );

        if (checkExisits && checkExisits.data === true) {
          this.loadingVisible = false;
          const confirmResult = await this.$confirm(
            `Production Planning already exists for the week ${this.model.ToWeek}. Do you want to overwrite?`,
            "Confirm Overwrite",
            {
              width: 400,
            }
          );

          if (confirmResult) {
            this.saveCopyProductionPlanning();
          }
        } else {
          this.saveCopyProductionPlanning();
        }
      } catch (error) {
        this.loadingVisible = false;
        this.displayServerError(error);
      }
    },

    /**
     * Save Copy Production Planning
     */
    async saveCopyProductionPlanning() {
      this.loadingVisible = true;
      try {
        const response =
          await ProductionPlanningService.saveCopyProductionPlanning({
            CompanyCode: this.model.CompanyCode,
            BranchCode: this.model.BranchCode,
            DivisionCode: this.model.DivisionCode,
            SourceWeekStartDate:
              this.getWeekRange(this.popUpConfig.DateIn).startDate +
              "T00:00:00",
            DestinationWeekStartDate:
              this.getWeekRange(this.model.DateIn).startDate + "T00:00:00",
          });

        if (response) {
          this.$notify(
            `Production Planning copied successfully to week ${this.model.ToWeek}`,
            "success"
          );

          this.$emit("refreshPlanning", this.model.DateIn);
        }
      } catch (error) {
        this.loadingVisible = false;
        this.displayServerError(error);
      } finally {
        this.loadingVisible = false;
        this.$store.dispatch("hotKey/setHotKeyPopupMode", false);
        this.$emit("setCopyPlanningVisible", false);
      }
    },
  },
  computed: {
    ...mapGetters({ getHotKeySave: "hotKey/getHotKeySave" }),
    ...mapGetters({
      companyName: "profile/companyName",
    }),
    /**
     * Get Toolbar Items
     * @returns
     */
    getToolbarItems() {
      return [
        {
          widget: "dxButton",
          location: "after",
          toolbar: "top",
          options: {
            text: "Copy Plan",
            onClick: this.onCopyHandler,
          },
        },
        {
          widget: "dxButton",
          location: "after",
          toolbar: "top",
          visible: true,
          options: {
            icon: "close",
            onClick: () => {
              this.$store.dispatch("hotKey/setHotKeyPopupMode", false);
              this.$emit("setCopyPlanningVisible", false);
            },
          },
        },
      ];
    },

    /**
     * Get Title
     */
    getTitle() {
      return this.copyTitle;
    },

    /**
     * Get Model Height
     */
    getModelHeight() {
      return 205;
    },

    /**
     * Get Form Height
     */
    getFormHeight() {
      return 100;
    },

    /**
     * Get Form Items
     */
    getFormItems() {
      return [
        {
          itemType: "group",
          caption: "",
          cssClass: "header-form-items",
          colCount: 2,
          items: [
            {
              label: { text: "Company" },
              dataField: "CompanyName",
              editorType: "dxTextBox",
              visible: false,

              editorOptions: {
                tabIndex: -1,
                readOnly: true,
              },
            },
            {
              label: { text: "Current Week" },
              dataField: "CurrentWeek",
              editorType: "dxTextBox",
              visible: false,

              editorOptions: {
                tabIndex: -1,
                readOnly: true,
              },
            },
            {
              label: { text: "Branch" },
              dataField: "BranchName",
              editorType: "dxTextBox",
              editorOptions: {
                tabIndex: -1,
                readOnly: true,
              },
            },
            {
              screenField: "DateIn",
              label: { text: "Date" },
              dataField: "DateIn",
              editorType: "dxDateBox",
              editorOptions: {
                onValueChanged: (e) => {
                  if (!e.event) return;
                  this.onCopyDateChanged(e);
                },
                onKeyDown: (e) => {
                  if (!e.event) return;
                  if (forwardTabbing(e)) {
                    e.event.preventDefault();
                  }
                },
              },
              required: true,
            },
            {
              label: { text: "Division" },
              dataField: "DivisionName",
              editorType: "dxTextBox",
              editorOptions: {
                tabIndex: -1,
                readOnly: true,
              },
            },
            {
              label: { text: "Week" },
              dataField: "ToWeek",
              editorType: "dxTextBox",
              editorOptions: {
                tabIndex: -1,
                readOnly: true,
              },
            },
          ],
        },
      ];
    },
  },
  watch: {
    /**
     * Watcher for the getHotKeySave computed property
     * Triggers the onCopyHandler method when getHotKeySave is true
     * @param {boolean} val - The new value of getHotKeySave
     * @returns {void}
     */
    getHotKeySave: {
      handler(val) {
        if (val) {
          this.$store.dispatch("hotKey/setHotKeySave", false);
          this.onCopyHandler(true);
        }
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss">
.gc-popup-copy-bom {
  .dx-toolbar.dx-popup-title {
    height: 60px;
  }
  .dx-toolbar-after {
    > div:first-child.dx-item.dx-toolbar-button {
      div.dx-button {
        border-radius: 2px !important;
        background-color: #03a9f4;
        color: #fff;
        -webkit-box-shadow: 0 1px 3px rgba(0, 0, 0, 0.24);
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.24);
        .dx-icon.dx-icon-spindown.dx-icon-right {
          margin-left: unset !important;
          color: #fff;
        }

        .dx-icon.dx-icon-spindown.dx-icon-right::before {
          color: #fff;
        }
      }
    }
  }

  .dx-form {
    .gc-form-group {
      .dx-form-group-caption {
        font-family: Roboto;
        font-weight: 700;
        font-size: 14px;
        line-height: 20px;
        letter-spacing: 0%;
        color: var(--colors-text-text-primary-900, #101828);
      }
    }
  }

  .new-bom {
    padding-top: unset !important;
  }

  .margin-top {
    margin-top: 10px;
  }
}
</style>
